import React, { useState, useCallback } from 'react';
import { Upload, FileCode, Info, Layers, ChevronDown, ChevronUp } from 'lucide-react';

import './Home.css';

const Home = () => {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const parseELF = (buffer) => {
    const view = new DataView(buffer);
    const magic = Array.from(new Uint8Array(buffer, 0, 4)).map(b => b.toString(16).padStart(2, '0')).join('');
    
    if (magic !== '7f454c46') return null;

    const is64bit = view.getUint8(4) === 2;
    const isLittleEndian = view.getUint8(5) === 1;
    
    const readAddr = (offset) => {
      if (is64bit) {
        return isLittleEndian ? view.getBigUint64(offset, true) : view.getBigUint64(offset, false);
      }
      return isLittleEndian ? view.getUint32(offset, true) : view.getUint32(offset, false);
    };

    const readHalf = (offset) => isLittleEndian ? view.getUint16(offset, true) : view.getUint16(offset, false);
    
    const headerSize = is64bit ? 64 : 52;
    const phoff = Number(readAddr(is64bit ? 32 : 28));
    const shoff = Number(readAddr(is64bit ? 40 : 32));
    const phentsize = readHalf(is64bit ? 54 : 42);
    const phnum = readHalf(is64bit ? 56 : 44);
    const shentsize = readHalf(is64bit ? 58 : 46);
    const shnum = readHalf(is64bit ? 60 : 48);

    const sections = [];
    
    sections.push({
      name: 'ELF Header',
      offset: 0,
      size: headerSize,
      color: '#3b82f6',
      description: 'ELF file header with magic number and metadata'
    });

    if (phoff > 0 && phnum > 0) {
      sections.push({
        name: 'Program Headers',
        offset: phoff,
        size: phentsize * phnum,
        color: '#10b981',
        description: `${phnum} program headers defining segments`
      });

      for (let i = 0; i < phnum; i++) {
        const phAddr = phoff + i * phentsize;
        const type = view.getUint32(phAddr, isLittleEndian);
        const offset = Number(readAddr(phAddr + (is64bit ? 8 : 4)));
        const filesz = Number(readAddr(phAddr + (is64bit ? 32 : 16)));
        
        const segmentTypes = ['NULL', 'LOAD', 'DYNAMIC', 'INTERP', 'NOTE', 'SHLIB', 'PHDR', 'TLS'];
        const typeName = type < segmentTypes.length ? segmentTypes[type] : `UNKNOWN(${type})`;
        
        if (filesz > 0 && offset < buffer.byteLength) {
          sections.push({
            name: `Segment: ${typeName}`,
            offset,
            size: filesz,
            color: '#8b5cf6',
            description: `Program segment of type ${typeName}`
          });
        }
      }
    }

    if (shoff > 0 && shnum > 0) {
      sections.push({
        name: 'Section Headers',
        offset: shoff,
        size: shentsize * shnum,
        color: '#f59e0b',
        description: `${shnum} section headers`
      });
    }

    return {
      format: 'ELF',
      architecture: is64bit ? '64-bit' : '32-bit',
      endianness: isLittleEndian ? 'Little Endian' : 'Big Endian',
      sections: sections.sort((a, b) => a.offset - b.offset)
    };
  };

  const parsePE = (buffer) => {
    const view = new DataView(buffer);
    const magic = view.getUint16(0, true);
    
    if (magic !== 0x5A4D) return null;

    const peOffset = view.getUint32(0x3C, true);
    const peSignature = view.getUint32(peOffset, true);
    
    if (peSignature !== 0x4550) return null;

    const machine = view.getUint16(peOffset + 4, true);
    const numberOfSections = view.getUint16(peOffset + 6, true);
    const optionalHeaderSize = view.getUint16(peOffset + 20, true);
    const is64bit = view.getUint16(peOffset + 24, true) === 0x20b;

    const sections = [];
    
    sections.push({
      name: 'DOS Header',
      offset: 0,
      size: 64,
      color: '#3b82f6',
      description: 'DOS MZ header for backward compatibility'
    });

    sections.push({
      name: 'PE Header',
      offset: peOffset,
      size: 24,
      color: '#10b981',
      description: 'PE signature and COFF header'
    });

    sections.push({
      name: 'Optional Header',
      offset: peOffset + 24,
      size: optionalHeaderSize,
      color: '#f59e0b',
      description: `Optional header (${is64bit ? 'PE32+' : 'PE32'})`
    });

    const sectionTableOffset = peOffset + 24 + optionalHeaderSize;
    
    for (let i = 0; i < numberOfSections; i++) {
      const sectionOffset = sectionTableOffset + i * 40;
      const nameBytes = new Uint8Array(buffer, sectionOffset, 8);
      const name = String.fromCharCode(...nameBytes).replace(/\0/g, '');
      const virtualSize = view.getUint32(sectionOffset + 8, true);
      const rawDataPtr = view.getUint32(sectionOffset + 20, true);
      const rawDataSize = view.getUint32(sectionOffset + 16, true);

      const colors = {
        '.text': '#8b5cf6',
        '.data': '#ec4899',
        '.rdata': '#06b6d4',
        '.rsrc': '#f97316',
        '.reloc': '#84cc16'
      };

      sections.push({
        name: `Section: ${name}`,
        offset: rawDataPtr,
        size: rawDataSize,
        color: colors[name] || '#64748b',
        description: `${name} section (Virtual Size: ${virtualSize} bytes)`
      });
    }

    return {
      format: 'PE',
      architecture: is64bit ? '64-bit' : '32-bit',
      machine: machine === 0x8664 ? 'x64' : machine === 0x14c ? 'x86' : `Unknown(${machine})`,
      sections: sections.sort((a, b) => a.offset - b.offset)
    };
  };

  const parseMachO = (buffer) => {
    const view = new DataView(buffer);
    const magic = view.getUint32(0, false);
    
    const validMagics = [0xfeedface, 0xfeedfacf, 0xcefaedfe, 0xcffaedfe];
    if (!validMagics.includes(magic)) return null;

    const is64bit = magic === 0xfeedfacf || magic === 0xcffaedfe;
    const isLittleEndian = magic === 0xcefaedfe || magic === 0xcffaedfe;

    const readUint32 = (offset) => view.getUint32(offset, isLittleEndian);
    
    const cputype = readUint32(4);
    const ncmds = readUint32(16);
    const sizeofcmds = readUint32(20);

    const sections = [];
    const headerSize = is64bit ? 32 : 28;

    sections.push({
      name: 'Mach-O Header',
      offset: 0,
      size: headerSize,
      color: '#3b82f6',
      description: 'Mach-O file header with magic number'
    });

    let cmdOffset = headerSize;
    for (let i = 0; i < ncmds; i++) {
      const cmd = readUint32(cmdOffset);
      const cmdsize = readUint32(cmdOffset + 4);

      if (cmd === 1 || cmd === 25) {
        const segname = new Uint8Array(buffer, cmdOffset + 8, 16);
        const name = String.fromCharCode(...segname).replace(/\0/g, '');
        const fileoff = is64bit ? Number(view.getBigUint64(cmdOffset + 40, isLittleEndian)) : readUint32(cmdOffset + 32);
        const filesize = is64bit ? Number(view.getBigUint64(cmdOffset + 48, isLittleEndian)) : readUint32(cmdOffset + 36);

        const colors = {
          '__TEXT': '#8b5cf6',
          '__DATA': '#ec4899',
          '__LINKEDIT': '#06b6d4',
          '__PAGEZERO': '#64748b'
        };

        if (filesize > 0) {
          sections.push({
            name: `Segment: ${name}`,
            offset: fileoff,
            size: filesize,
            color: colors[name] || '#f59e0b',
            description: `${name} segment (${cmd === 25 ? '64-bit' : '32-bit'})`
          });
        }
      }

      cmdOffset += cmdsize;
    }

    return {
      format: 'Mach-O',
      architecture: is64bit ? '64-bit' : '32-bit',
      cpu: cputype === 7 ? 'x86' : cputype === 0x01000007 ? 'x86_64' : cputype === 12 ? 'ARM' : cputype === 0x0100000c ? 'ARM64' : `Unknown(${cputype})`,
      sections: sections.sort((a, b) => a.offset - b.offset)
    };
  };

  const analyzeFile = useCallback((file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const buffer = e.target.result;
      
      let result = parseELF(buffer) || parsePE(buffer) || parseMachO(buffer);
      
      if (!result) {
        result = {
          format: 'Unknown',
          architecture: 'Unknown',
          sections: [{
            name: 'Raw Data',
            offset: 0,
            size: buffer.byteLength,
            color: '#64748b',
            description: 'Unrecognized binary format'
          }]
        };
      }

      setAnalysis({
        ...result,
        fileName: file.name,
        fileSize: buffer.byteLength,
        buffer: new Uint8Array(buffer)
      });
    };

    reader.readAsArrayBuffer(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      analyzeFile(droppedFile);
    }
  }, [analyzeFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      analyzeFile(selectedFile);
    }
  }, [analyzeFile]);

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatHex = (num) => `0x${num.toString(16).toUpperCase()}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Binary Analyzer
          </h1>
          <p className="text-slate-400">Analyze ELF, PE, and Mach-O binary files</p>
        </div>

        {!analysis ? (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`relative border-2 border-dashed rounded-2xl transition-all duration-300 ${
              isDragging
                ? 'border-blue-500 bg-blue-500/10 scale-105'
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <input
              type="file"
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              accept="*"
            />
            <div className="flex flex-col items-center justify-center py-24 px-8">
              <Upload className={`w-16 h-16 mb-4 transition-colors ${isDragging ? 'text-blue-400' : 'text-slate-500'}`} />
              <p className="text-xl font-medium mb-2">Drop your binary file here</p>
              <p className="text-slate-500">or click to browse</p>
              <div className="flex gap-4 mt-6 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  ELF (Linux)
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  PE (Windows)
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  Mach-O (macOS)
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileCode className="w-8 h-8 text-blue-400" />
                  <div>
                    <h2 className="text-2xl font-bold">{analysis.fileName}</h2>
                    <p className="text-slate-400">{formatBytes(analysis.fileSize)}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setAnalysis(null);
                    setFile(null);
                    setExpandedSections({});
                  }}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  Analyze New File
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-500 text-sm mb-1">Format</p>
                  <p className="text-lg font-semibold">{analysis.format}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-500 text-sm mb-1">Architecture</p>
                  <p className="text-lg font-semibold">{analysis.architecture}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-500 text-sm mb-1">Sections</p>
                  <p className="text-lg font-semibold">{analysis.sections.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="w-5 h-5 text-purple-400" />
                <h3 className="text-xl font-bold">Memory Layout</h3>
              </div>
              
              <div className="relative h-16 bg-slate-900 rounded-lg overflow-hidden mb-6">
                {analysis.sections.map((section, idx) => {
                  const start = (section.offset / analysis.fileSize) * 100;
                  const width = (section.size / analysis.fileSize) * 100;
                  
                  return (
                    <div
                      key={idx}
                      className="absolute h-full cursor-pointer transition-opacity hover:opacity-80"
                      style={{
                        left: `${start}%`,
                        width: `${width}%`,
                        backgroundColor: section.color
                      }}
                      title={`${section.name}: ${formatHex(section.offset)} - ${formatHex(section.offset + section.size)}`}
                    />
                  );
                })}
              </div>

              <div className="space-y-2">
                {analysis.sections.map((section, idx) => (
                  <div key={idx} className="bg-slate-900/50 rounded-lg overflow-hidden">
                    <div
                      className="p-4 cursor-pointer hover:bg-slate-900/70 transition-colors"
                      onClick={() => toggleSection(idx)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: section.color }}
                          />
                          <div>
                            <p className="font-medium">{section.name}</p>
                            <p className="text-sm text-slate-500">{section.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            <p className="text-slate-400">Offset: {formatHex(section.offset)}</p>
                            <p className="text-slate-500">Size: {formatBytes(section.size)}</p>
                          </div>
                          {expandedSections[idx] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>
                    
                    {expandedSections[idx] && (
                      <div className="p-4 bg-slate-950/50 border-t border-slate-800">
                        <div className="font-mono text-xs space-y-1">
                          {Array.from(analysis.buffer.slice(section.offset, Math.min(section.offset + 256, section.offset + section.size)))
                            .reduce((rows, byte, i) => {
                              const rowIdx = Math.floor(i / 16);
                              if (!rows[rowIdx]) rows[rowIdx] = [];
                              rows[rowIdx].push(byte);
                              return rows;
                            }, [])
                            .map((row, i) => (
                              <div key={i} className="flex gap-4">
                                <span className="text-slate-600 w-20">{formatHex(section.offset + i * 16)}</span>
                                <span className="text-blue-400">
                                  {row.map(b => b.toString(16).padStart(2, '0')).join(' ')}
                                </span>
                                <span className="text-slate-500">
                                  {row.map(b => (b >= 32 && b <= 126 ? String.fromCharCode(b) : '.')).join('')}
                                </span>
                              </div>
                            ))}
                          {section.size > 256 && (
                            <p className="text-slate-600 mt-2">... and {formatBytes(section.size - 256)} more</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-blue-400" />
                <h3 className="text-xl font-bold">Analysis Details</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-slate-400">Total Size:</span>
                  <span className="font-mono">{formatBytes(analysis.fileSize)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-slate-400">Format:</span>
                  <span className="font-mono">{analysis.format}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-slate-400">Architecture:</span>
                  <span className="font-mono">{analysis.architecture}</span>
                </div>
                {analysis.endianness && (
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-slate-400">Endianness:</span>
                    <span className="font-mono">{analysis.endianness}</span>
                  </div>
                )}
                {analysis.machine && (
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-slate-400">Machine:</span>
                    <span className="font-mono">{analysis.machine}</span>
                  </div>
                )}
                {analysis.cpu && (
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-slate-400">CPU Type:</span>
                    <span className="font-mono">{analysis.cpu}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
